import React, { useEffect } from "react";
import { Row, Col, Button, Form, Switch, Modal as AntModal } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import Constants from "../../Utils/Constants";
import useToken from "../../Authentication/useToken";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ProfessionalDetailsIdentityFields from "../shared/ProfessionalDetailsIdentityFields";

const doctorDetailsFieldKeys = {
  labelFirstName: "Physicians.label_first_name",
  errorRequiredFirstName: "Physicians.error_required_first_name",
  errorFirstNameMinLength: "Physicians.error_first_name_min_length",
  labelLastName: "Physicians.label_last_name",
  errorRequiredLastName: "Physicians.error_required_last_name",
  errorLastNameMinLength: "Physicians.error_last_name_min_length",
  labelEmail: "Physicians.label_email",
  errorRequiredEmail: "Physicians.error_required_email",
  errorInvalidEmailFormat: "Physicians.error_invalid_email_format",
  labelPhoneNumber: "Physicians.label_phone_number",
  errorRequiredPhoneNumber: "Physicians.error_required_phone_number",
  errorInvalidPhoneNumber: "Physicians.error_invalid_phone_number",
};

function DoctorDetails({ doctor, onClose, refetchDoctors }) {
  const { t } = useTranslation("Professionals");
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();
  const { token } = useToken();

  // Pré-remplir le formulaire avec les données du médecin
  useEffect(() => {
    if (doctor) {
      setValue("firstname", doctor.firstname);
      setValue("lastname", doctor.lastname);
      setValue("email", doctor.email);
      setValue("phoneNumber", doctor.phoneNumber);
      setValue("active", doctor.active);
    }
  }, [doctor, setValue]);

  const onSubmit = (data) => {
    const doctorData = {
      ...data,
      role: "doctor",
    };

    axios
      .put(
        `${Constants.SERVER_URL}/update-professional-user/${doctor.id}`,
        doctorData,
        {
          headers: { Authorization: "Bearer " + token },
        }
      )
      .then(() => {
        refetchDoctors();
        AntModal.success({
          content: t("Physicians.success_updating_msg"),
          okText: t("Physicians.button_close"),
          centered: true,
          onOk: () => {
            onClose();
          },
        });
      })
      .catch((err) => {
        const errorMessage =
          t(`Backend:${err.response?.data?.message}`) ||
          t("Physicians.error_updating_physician");
        AntModal.error({
          content: errorMessage,
          okText: t("Physicians.button_close"),
          centered: true,
        });
      });
  };

  return (
    <Row justify="center">
      <Col span={16}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <ProfessionalDetailsIdentityFields
            t={t}
            control={control}
            errors={errors}
            keys={doctorDetailsFieldKeys}
          />

          <Row gutter={16}>
            {/* Status field */}
            <Col span={12}>
              <Form.Item label={t("Physicians.label_status")}>
                <Controller
                  name="active"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      checkedChildren={t("Physicians.active_status")}
                      unCheckedChildren={t("Physicians.inactive_status")}
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              {t("Physicians.button_update_physician")}
            </Button>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  );
}

DoctorDetails.propTypes = {
  doctor: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  refetchDoctors: PropTypes.func.isRequired,
};

export default DoctorDetails;
